import moment from 'moment';
import React, { JSX, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { track } from '~/src/dash/analytics/analytics';

import {
  AuctionInsightKey,
  AuctionInsightsData,
  getCampaignAuctionInsights,
} from '~/src/dash/apiV2/campaigns';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { Sankey } from '~/src/dash/components/charts/Sankey';
import {
  IEChartsSankeyData,
  IEChartsSankeyLabelParams,
  IEChartsSankeyLink,
  IEChartsSankeyNode,
} from '~/src/dash/components/charts/Sankey/common';
import {
  formatLinkTooltip,
  formatNodeTooltip,
} from '~/src/dash/components/charts/Sankey/customTooltips';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import { Row, Rows, usePublicaTranslation } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { CampaignAuctionInsightsFilters } from '~/src/dash/views/campaigns/line-item/CampaignAuctionInsightsFilters';
import { campaignAuctionInsightsNodes } from '~/src/dash/views/campaigns/line-item/config';

const formatData = (data: AuctionInsightsData): IEChartsSankeyData => {
  const sankeyLinks: IEChartsSankeyLink[] = [];
  const nodesInUse = new Set<AuctionInsightKey>();

  for (const link of data?.links || []) {
    if (link.value && link.value > 0) {
      sankeyLinks.push({
        source: link.source,
        target: link.target,
        value: link.value,
      });
      nodesInUse.add(link.source);
      nodesInUse.add(link.target);
    }
  }

  const sankeyNodes: IEChartsSankeyNode[] = Array.from(nodesInUse)
    .sort((a, b) => {
      const orderA = campaignAuctionInsightsNodes[a]?.order || 99;
      const orderB = campaignAuctionInsightsNodes[b]?.order || 99;
      return orderA - orderB;
    })
    .map((nodeId) => {
      const nodeConfig = campaignAuctionInsightsNodes[nodeId];
      return {
        name: nodeId,
        value: data.nodes[nodeId]?.total || 0,
        depth: nodeConfig?.depth,
        itemStyle: {
          color: nodeConfig?.color || publicaColors.lightGray,
        },
        nodeData: data.nodes[nodeId],
      };
    });

  return {
    nodes: sankeyNodes,
    links: sankeyLinks,
  };
};

const getInitialDates = (
  endDate: string,
): { initialStartDate: moment.Moment; initialEndDate: moment.Moment } => {
  const today = moment();
  const momentEndDate = moment(endDate);

  if (today.isAfter(momentEndDate)) {
    return {
      initialStartDate: momentEndDate.clone().startOf('day'),
      initialEndDate: momentEndDate.clone().endOf('day').hours(23).minutes(0).seconds(0),
    };
  } else {
    return {
      initialStartDate: today.clone().startOf('day'),
      initialEndDate: today.clone().endOf('day').hours(23).minutes(0).seconds(0),
    };
  }
};

interface IProps {
  campaignID: number;
  startDate: string;
  endDate: string;
}

export const CampaignAuctionInsights = ({
  campaignID,
  startDate,
  endDate,
}: IProps): JSX.Element => {
  const { session, isSuperUser, user } = useBaseStore();
  const { t } = usePublicaTranslation();

  const { initialStartDate, initialEndDate } = getInitialDates(endDate);

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [data, setData] = useState<IEChartsSankeyData>({ nodes: [], links: [] });
  const [calStartDate, setCalStartDate] = useState<moment.Moment>(initialStartDate);
  const [calEndDate, setCalEndDate] = useState<moment.Moment>(initialEndDate);

  const hasCampaignNotStarted = moment().isBefore(startDate);

  const fetchData = (): void => {
    const queryStart = moment().unix();
    setLoadState(LoadingStateLoading);
    getCampaignAuctionInsights(session.publisher.ID, {
      PublisherID: Number(session.publisher.ID),
      CampaignID: campaignID,
      StartDate: calStartDate.format('YYYY-MM-DD HH:mm:ss'),
      EndDate: calEndDate.format('YYYY-MM-DD HH:mm:ss'),
      Timezone: session.publisher.Timezone,
    })
      .then((data: AuctionInsightsData) => {
        const formattedData = data ? formatData(data) : { nodes: [], links: [] };
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setData(formattedData);
        });
        track(
          'Campaign Auction Insights - Data Retrieved',
          { isSuperUser, queryTime: moment().unix() - queryStart },
          user.user,
        );
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setData({ nodes: [], links: [] });
        });
        console.error(err);
      });
  };

  const handleApplyDatesChange = ({ newStartDate, newEndDate }: INewCalDates): void => {
    unstable_batchedUpdates(() => {
      setCalStartDate(newStartDate.clone().startOf('day'));
      setCalEndDate(newEndDate.clone().endOf('day').hours(23).minutes(0).seconds(0));
    });
    track('Campaign Auction Insights - Dates Changed', { isSuperUser }, user.user);
  };

  useEffect(() => {
    fetchData();
  }, [calStartDate, calEndDate]);

  return (
    <Rows>
      <Row largeHorizontalPadding smallVerticalPadding>
        <CampaignAuctionInsightsFilters
          calStartDate={calStartDate}
          calEndDate={calEndDate}
          handleApplyDatesChange={handleApplyDatesChange}
          loadState={loadState}
          campaignStartDate={moment(startDate)}
          campaignEndDate={moment(endDate)}
          isDisabled={hasCampaignNotStarted}
        />
      </Row>
      <Row largeHorizontalPadding noVerticalPadding>
        <Rows style={{ ...highlightedDivStyle }}>
          <Row>
            <Sankey
              titleTKey='CampaignAuctionInsights'
              loadState={loadState}
              nodes={data.nodes}
              links={data.links}
              labelFormatter={(params: IEChartsSankeyLabelParams) => {
                const nodeConfig = campaignAuctionInsightsNodes[params.name as AuctionInsightKey];
                const label = nodeConfig?.tKey ? t(nodeConfig.tKey) : params.name;
                const nodeData = params.data.nodeData;
                const depth = params.data.depth ?? 0;
                const percentage: number = (nodeData?.percentage as number) ?? 0;
                const formattedPercentage: string = percentage.toFixed(2);
                const formattedValue = params.value.toLocaleString('en-US');
                // Don't show percentage for depth 0 nodes
                if (depth === 0) {
                  return `{labelName|${label}}\n{labelValue|${formattedValue}}`;
                }
                return `{labelName|${label}}\n{labelValue|${formattedValue} - ${formattedPercentage}%}`;
              }}
              nodeTooltipFormatter={formatNodeTooltip}
              linkTooltipFormatter={formatLinkTooltip}
              nodeConfigMap={campaignAuctionInsightsNodes}
              customNoDataMessageTKey={
                hasCampaignNotStarted
                  ? 'DataWillBeAvailableOnceTheCampaignBegins'
                  : 'NoDataToDisplay'
              }
            />
          </Row>
        </Rows>
      </Row>
    </Rows>
  );
};
